import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PeopleIcon from "@mui/icons-material/People";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import LanguageIcon from "@mui/icons-material/Language";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";

type CountBag = Record<string, any> | undefined;

/** Line 1 — pre_appointment_status (All = no PAS filter). */
const PRE_CHIPS: Array<{
  key: string;
  label: string;
  sourceKey: string;
  icon: typeof ScheduleIcon;
  activeClass: string;
  idleClass: string;
}> = [
  {
    key: "all",
    label: "All",
    sourceKey: "all",
    icon: AllInclusiveIcon,
    activeClass: "bg-slate-700 text-white border-slate-700 shadow-sm",
    idleClass:
      "bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:border-slate-400",
  },
  {
    key: "appointment",
    label: "Appointment",
    sourceKey: "appointment",
    icon: ScheduleIcon,
    activeClass: "bg-orange-500 text-white border-orange-500 shadow-sm",
    idleClass:
      "bg-white dark:bg-slate-700 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700 hover:border-orange-400",
  },
  {
    key: "walk_in",
    label: "Walk-in",
    sourceKey: "walk_in",
    icon: DirectionsWalkIcon,
    activeClass: "bg-teal-500 text-white border-teal-500 shadow-sm",
    idleClass:
      "bg-white dark:bg-slate-700 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-700 hover:border-teal-400",
  },
  {
    key: "call_in",
    label: "Call-in",
    sourceKey: "call_in",
    icon: PhoneInTalkIcon,
    activeClass: "bg-cyan-500 text-white border-cyan-500 shadow-sm",
    idleClass:
      "bg-white dark:bg-slate-700 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700 hover:border-cyan-400",
  },
  {
    key: "online",
    label: "Online",
    sourceKey: "online",
    icon: LanguageIcon,
    activeClass: "bg-indigo-500 text-white border-indigo-500 shadow-sm",
    idleClass:
      "bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700 hover:border-indigo-400",
  },
  {
    key: "cancel",
    label: "Cancel",
    sourceKey: "cancel",
    icon: CancelIcon,
    activeClass: "bg-red-500 text-white border-red-500 shadow-sm",
    idleClass:
      "bg-white dark:bg-slate-700 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700 hover:border-red-400",
  },
];

/** Line 2 — lifecycle status. */
const STATUS_CARDS: Array<{
  key: string;
  label: string;
  statusKey: string;
  icon: typeof EventAvailableIcon;
  bg: string;
  text: string;
  iconBg: string;
  ring: string;
}> = [
  {
    key: "upcoming",
    label: "Upcoming",
    statusKey: "upcoming",
    icon: EventAvailableIcon,
    bg: "bg-yellow-100 dark:bg-yellow-900/40",
    text: "text-yellow-800 dark:text-yellow-300",
    iconBg: "bg-yellow-500",
    ring: "ring-yellow-400/60",
  },
  {
    key: "in_queue",
    label: "In Queue",
    statusKey: "in_queue",
    icon: PeopleIcon,
    bg: "bg-sky-100 dark:bg-sky-900/40",
    text: "text-sky-700 dark:text-sky-300",
    iconBg: "bg-sky-500",
    ring: "ring-sky-400/60",
  },
  {
    key: "ongoing",
    label: "Ongoing",
    statusKey: "ongoing",
    icon: PlayCircleIcon,
    bg: "bg-lime-100 dark:bg-lime-900/40",
    text: "text-lime-700 dark:text-lime-300",
    iconBg: "bg-lime-500",
    ring: "ring-lime-400/60",
  },
  {
    key: "complete",
    label: "Complete",
    statusKey: "complete",
    icon: CheckCircleIcon,
    bg: "bg-emerald-100 dark:bg-emerald-900/40",
    text: "text-emerald-700 dark:text-emerald-300",
    iconBg: "bg-emerald-500",
    ring: "ring-emerald-400/60",
  },
  {
    key: "no_show",
    label: "No-Show",
    statusKey: "no_show",
    icon: VisibilityOffIcon,
    bg: "bg-slate-100 dark:bg-slate-700",
    text: "text-slate-700 dark:text-slate-200",
    iconBg: "bg-slate-500",
    ring: "ring-slate-400/60",
  },
  {
    key: "rescheduled",
    label: "Rescheduled",
    statusKey: "rescheduled",
    icon: EventRepeatIcon,
    bg: "bg-cyan-100 dark:bg-cyan-900/40",
    text: "text-cyan-700 dark:text-cyan-300",
    iconBg: "bg-cyan-500",
    ring: "ring-cyan-400/60",
  },
];

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function resolveAllCount(count: CountBag): number {
  const byView = count?.by_view;
  if (byView && typeof byView === "object" && byView.all != null) {
    return num(byView.all);
  }
  return num(count?.appointments ?? count?.total);
}

function resolveStatusCount(count: CountBag, statusKey: string): number {
  const byStatus = count?.by_status;
  if (byStatus && typeof byStatus === "object") {
    if (statusKey === "in_queue") return num(byStatus.in_queue ?? byStatus.inqueue);
    if (statusKey === "complete") return num(byStatus.complete ?? byStatus.completed);
    if (statusKey === "no_show") return num(byStatus.no_show ?? byStatus.noshow);
    if (statusKey === "rescheduled") {
      return num(byStatus.rescheduled ?? byStatus.tray ?? byStatus.reschudle);
    }
    return num(byStatus[statusKey]);
  }
  if (statusKey === "upcoming") {
    return num(count?.upcoming ?? count?.pending ?? count?.appointment);
  }
  if (statusKey === "in_queue") return num(count?.in_queue ?? count?.inqueue);
  if (statusKey === "complete") return num(count?.complete ?? count?.completed);
  if (statusKey === "no_show") return num(count?.no_show ?? count?.noshow);
  if (statusKey === "rescheduled") {
    return num(count?.rescheduled ?? count?.tray ?? count?.reschudle);
  }
  return num(count?.[statusKey]);
}

function resolveSourceCount(count: CountBag, sourceKey: string): number {
  if (sourceKey === "all") return resolveAllCount(count);

  const bySource = count?.by_source;
  if (bySource && typeof bySource === "object") {
    if (sourceKey === "appointment" || sourceKey === "pre_booked") {
      return num(
        bySource.appointment ?? bySource.pre_booked ?? bySource.schedule
      );
    }
    if (sourceKey === "walk_in") return num(bySource.walk_in ?? bySource.walkin);
    if (sourceKey === "call_in") return num(bySource.call_in ?? bySource.callin);
    if (sourceKey === "cancel") {
      return num(
        bySource.cancel ??
          bySource.cancelled ??
          count?.by_status?.cancelled ??
          count?.cancelled
      );
    }
    return num(bySource[sourceKey]);
  }
  if (sourceKey === "appointment" || sourceKey === "pre_booked") {
    return num(count?.appointment ?? count?.pre_booked ?? count?.schedule);
  }
  if (sourceKey === "walk_in") return num(count?.walk_in ?? count?.walkin);
  if (sourceKey === "call_in") return num(count?.call_in ?? count?.callin);
  if (sourceKey === "cancel") {
    return num(count?.cancel ?? count?.cancelled ?? count?.canceled);
  }
  return num(count?.[sourceKey]);
}

function AppointmentCounts({
  count,
  activeSource = "all",
  activeStatus = "",
  onSourceSelect,
  onStatusSelect,
}: {
  count: CountBag;
  /** @deprecated unused — kept so older call sites type-check during migrate */
  compact?: boolean;
  /** Pre-appointment line: all | appointment | walk_in | call_in | online | cancel */
  activeSource?: string;
  /** Lifecycle line: upcoming | in_queue | ongoing | complete | no_show | rescheduled */
  activeStatus?: string;
  onSourceSelect?: (sourceKey: string) => void;
  /** Click again clears (pass empty string). */
  onStatusSelect?: (statusKey: string) => void;
  /** @deprecated view tabs removed */
  activeView?: string;
  /** @deprecated */
  onViewSelect?: (viewKey: string) => void;
  /** @deprecated */
  activeKey?: string;
  /** @deprecated */
  onSelect?: (key: string) => void;
  /** @deprecated */
  selectableKeys?: string[];
}) {
  const preActive = !activeStatus && (activeSource || "all");

  return (
    <div className="space-y-3">
      {/* Line 1 — pre_appointment_status */}
      <div className="flex flex-wrap items-center gap-1.5">
        {PRE_CHIPS.map(
          ({ key, label, sourceKey, icon: Icon, activeClass, idleClass }) => {
            const isActive = preActive === key;
            const n = resolveSourceCount(count, sourceKey);
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSourceSelect?.(key)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  isActive ? activeClass : idleClass
                }`}
                aria-pressed={isActive}
                title={
                  isActive && key !== "all"
                    ? `Showing ${label}`
                    : key === "all"
                      ? "Show all appointments"
                      : `Filter by ${label}`
                }
              >
                <Icon sx={{ fontSize: 14 }} />
                {label}
                <span className="tabular-nums opacity-90">({n})</span>
              </button>
            );
          }
        )}
      </div>

      {/* Line 2 — lifecycle status */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5">
        {STATUS_CARDS.map(
          ({ key, label, statusKey, icon: Icon, bg, text, iconBg, ring }) => {
            const isActive = activeStatus === key;
            const n = resolveStatusCount(count, statusKey);
            return (
              <button
                key={key}
                type="button"
                onClick={() => onStatusSelect?.(isActive ? "" : key)}
                className={[
                  bg,
                  "rounded-lg px-2 py-2 flex items-center gap-1.5 min-w-0 transition-all duration-200 cursor-pointer hover:scale-[1.02] hover:shadow-md active:scale-[0.98]",
                  isActive ? `ring-2 ${ring} shadow-md scale-[1.02]` : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-pressed={isActive}
                title={
                  isActive
                    ? `Clear ${label.toLowerCase()} filter`
                    : `Show ${label.toLowerCase()} appointments`
                }
              >
                <div
                  className={`hidden sm:flex w-6 h-6 shrink-0 ${iconBg} rounded-md items-center justify-center shadow-sm`}
                >
                  <Icon className="text-white" sx={{ fontSize: 14 }} />
                </div>
                <div className="min-w-0 text-left">
                  <p
                    className={`text-sm sm:text-base font-bold ${text} leading-none tabular-nums`}
                  >
                    {n}
                  </p>
                  <p
                    className={`text-[8px] sm:text-[9px] font-medium ${text} uppercase tracking-wide truncate`}
                  >
                    {label}
                  </p>
                </div>
              </button>
            );
          }
        )}
      </div>
    </div>
  );
}

export default AppointmentCounts;
